import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { darkTheme, lightTheme, type Theme } from "@ortex-charts/core";
import { backgroundColorState, greyState, negativeState, neutralState, ortexColorState, positiveState, textColorState, themeState } from "@/lib/store";
import { alpha } from "./color";

export interface OrtexChartPalette {
    mode: "dark" | "light";
    background: string;
    text: string;
    grey: string;
    ortex: string;
    positive: string;
    negative: string;
    neutral: string;
}

/**
 * The library theme built from the app's own colours, so every chart follows
 * the Jotai theme exactly as the rest of the page does: the app background
 * behind the plot, the app text colour on the axes, ORTEX green as the first
 * series colour and the positive/negative pair for candles and volume.
 */
export function buildOrtexChartTheme(p: OrtexChartPalette): Theme {
    const base = p.mode === "light" ? lightTheme : darkTheme;
    const dark = p.mode !== "light";
    return {
        ...base,
        name: `ortex-${p.mode}`,
        // The chart sits on whatever panel holds it: no background of its own; the app colour is the surface outlines and blends use.
        background: "transparent",
        axisBackground: "transparent",
        surface: p.background,
        axisBorder: alpha(p.grey, 0.5),
        grid: alpha(p.grey, dark ? 0.16 : 0.22),
        paneSeparator: alpha(p.grey, 0.5),
        text: p.text,
        textMuted: p.grey,
        fontFamily: "Roboto, system-ui, sans-serif",
        crosshair: alpha(p.grey, 0.9),
        up: p.positive,
        down: p.negative,
        upWick: p.positive,
        downWick: p.negative,
        volumeUp: alpha(p.positive, 0.55),
        volumeDown: alpha(p.negative, 0.55),
        line: p.ortex,
        areaTop: alpha(p.ortex, 0.35),
        areaBottom: alpha(p.ortex, 0.02),
        palette: [p.ortex, "#4C8DFF", "#F0B429", "#B36AE2", p.negative, "#3DBFD9", "#FF8A3D", "#8FD14F", p.neutral, "#E06AA8"],
        legendText: p.text,
        tooltipBackground: dark ? "#0E1214" : "#FFFFFF",
        tooltipText: p.text,
        tooltipBorder: alpha(p.grey, 0.4),
    };
}

/** The current app palette as a library theme; memoised so charts only re-theme when a colour changes. */
export function useOrtexChartTheme(): Theme {
    const mode = useAtomValue(themeState);
    const background = useAtomValue(backgroundColorState);
    const text = useAtomValue(textColorState);
    const grey = useAtomValue(greyState);
    const ortex = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);
    return useMemo(() => buildOrtexChartTheme({ mode: mode === "light" ? "light" : "dark", background, text, grey, ortex, positive, negative, neutral }), [mode, background, text, grey, ortex, positive, negative, neutral]);
}

/** The palette itself, for components that colour things outside the canvas. */
export function useOrtexChartPalette(): OrtexChartPalette {
    const mode = useAtomValue(themeState);
    const background = useAtomValue(backgroundColorState);
    const text = useAtomValue(textColorState);
    const grey = useAtomValue(greyState);
    const ortex = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);
    return useMemo(() => ({ mode: mode === "light" ? "light" : "dark", background, text, grey, ortex, positive, negative, neutral }), [mode, background, text, grey, ortex, positive, negative, neutral]);
}
